import * as React from "react";
import type { BeforeInstallPromptEvent } from "@/lib/pwa/pwa-install";
import { isStandaloneDisplayMode } from "@/lib/pwa/pwa-install";

type PromptResult =
  | { kind: "unavailable" }
  | { kind: "dismissed" }
  | { kind: "accepted" };

// ---------------------------------------------------------------------------
// Module-level capture so we never miss beforeinstallprompt even if it fires
// before any React component mounts (proven race condition on desktop Chrome).
// ---------------------------------------------------------------------------
let deferredPromptCache: BeforeInstallPromptEvent | null = null;
const subscribers = new Set<(e: BeforeInstallPromptEvent | null) => void>();

function publish(event: BeforeInstallPromptEvent | null) {
  deferredPromptCache = event;
  for (const fn of subscribers) fn(event);
}

if (typeof globalThis !== "undefined") {
  globalThis.addEventListener("beforeinstallprompt", (event: Event) => {
    event.preventDefault();
    publish(event as BeforeInstallPromptEvent);
  });
}

/**
 * Snapshot of install-related runtime state, useful for diagnostics
 * when Chrome hasn't emitted the install prompt.
 */
export function getPwaInstallDiagnostics(): {
  isSecureContext: boolean;
  hasManifestLink: boolean;
  hasServiceWorkerSupport: boolean;
  hasBeforeInstallPromptSupport: boolean;
  hasCachedPrompt: boolean;
  isStandalone: boolean;
} {
  return {
    isSecureContext: globalThis.isSecureContext,
    hasManifestLink: document.querySelector('link[rel="manifest"]') != null,
    hasServiceWorkerSupport: "serviceWorker" in globalThis.navigator,
    hasBeforeInstallPromptSupport: "onbeforeinstallprompt" in globalThis,
    hasCachedPrompt: deferredPromptCache != null,
    isStandalone: isStandaloneDisplayMode(globalThis),
  };
}

export function usePwaInstall(): {
  canInstall: boolean;
  isInstalled: boolean;
  promptInstall: () => Promise<PromptResult>;
} {
  const [deferredPrompt, setDeferredPrompt] =
    React.useState<BeforeInstallPromptEvent | null>(deferredPromptCache);
  const [isInstalled, setIsInstalled] = React.useState(() =>
    isStandaloneDisplayMode(globalThis),
  );

  React.useEffect(() => {
    const onPrompt = (e: BeforeInstallPromptEvent | null) =>
      setDeferredPrompt(e);

    const onAppInstalled = () => {
      publish(null);
      setIsInstalled(true);
    };

    setIsInstalled(isStandaloneDisplayMode(globalThis));
    onPrompt(deferredPromptCache);

    subscribers.add(onPrompt);
    globalThis.addEventListener("appinstalled", onAppInstalled);

    return () => {
      subscribers.delete(onPrompt);
      globalThis.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const canInstall = Boolean(deferredPrompt) && !isInstalled;

  const promptInstall = React.useCallback(async (): Promise<PromptResult> => {
    if (!deferredPrompt) return { kind: "unavailable" };
    if (isStandaloneDisplayMode(globalThis)) {
      setIsInstalled(true);
      publish(null);
      return { kind: "unavailable" };
    }

    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      publish(null);
      if (choice.outcome === "accepted") {
        return { kind: "accepted" };
      }
      return { kind: "dismissed" };
    } catch {
      publish(null);
      return { kind: "unavailable" };
    }
  }, [deferredPrompt]);

  return { canInstall, isInstalled, promptInstall };
}
